import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { SectionFrame } from "../atoms/SectionFrame";
import { FadeIn } from "../atoms/FadeIn";

const EASE = [0.25, 0.1, 0.25, 1];

const FAQS = [
    {
        q: "What types of animation videos does GrowCo Motion create?",
        a: [
            "We create all kinds of animation that help businesses communicate better. From animated explainer videos and SaaS product demos to educational content, motion graphics, product launches, corporate videos, logo animations, YouTube content, podcasts, and social media videos, we've got you covered.",
            "Every project is built from the ground up to match your goals, audience, and brand. Whether you're explaining a complex product, launching a new feature, or telling your company's story, we'll help you find the right animation style to bring it to life.",
        ],
    },
    {
        q: "How much does an animation project cost?",
        a: [
            "Every animation project is different, so we provide custom quotes instead of fixed pricing.",
            "The cost depends on several factors, including the animation style, video length, production complexity, scripting, custom illustrations, timeline, voiceovers, and any additional deliverables. Rather than using a one size fits all pricing model, we tailor every proposal to your project's unique requirements.",
            "Once we understand your goals and vision, we'll recommend the best approach and provide a clear, transparent quote with no hidden surprises.",
        ],
    },
    {
        q: "How long does an animation project take?",
        a: [
            "The timeline depends on the scope of your project, but as a general guide, a two minute animated explainer video usually takes around 3 to 4 weeks from kickoff to final delivery.",
            "Need it sooner? Let us know. If your project has a launch date or a tight deadline, we'll always do our best to make it happen and discuss the best way to get there.",
        ],
    },
    {
        q: "What's included in the project?",
        a: [
            "Think of us as your animation partner from start to finish.",
            "Depending on your project, we can handle scripting, storyboarding, custom illustrations, animation, professional voiceovers, music, sound design, video editing, and final delivery in the formats you need.",
            "Need multiple aspect ratios, thumbnails, or other custom deliverables? We can absolutely help with those too as optional add ons.",
        ],
    },
    {
        q: "Will the animation match our brand?",
        a: [
            "Absolutely.",
            "Your brand already has a personality, and our job is to bring it to life through motion. We work with your existing brand guidelines, visual identity, and messaging to make sure every frame feels like it belongs to your business.",
            "We don't believe in cookie cutter templates. Every animation is designed to feel uniquely yours while adding a fresh layer of creativity that helps you stand out.",
        ],
    },
    {
        q: "How many revisions do I get?",
        a: [
            "We offer unlimited revisions within each approved production stage.",
            "That means we'll work closely with you to refine the script, storyboard, illustrations, and animation before moving on to the next step. It keeps the process smooth, collaborative, and ensures you're happy with every milestone along the way.",
        ],
    },
    {
        q: "How do I get started?",
        a: [
            "Getting started is easy.",
            "Book a discovery call or fill out our contact form with a few details about your project. We'll learn about your goals, audience, timeline, and vision, then recommend the best approach for your needs.",
            "From there, we'll send you a tailored proposal outlining the scope, timeline, and deliverables so we can get your project moving. We can't wait to create something amazing together.",
        ],
    },
];

export function FaqSection() {
    const [open, setOpen] = useState(0);
    return (
        <SectionFrame innerClassName="">
            <div className="py-20 md:py-28 px-6 md:px-12">
                <FadeIn y={40} className="mb-12 md:mb-16">
                    <div className="text-sm uppercase tracking-[0.3em] mb-4" style={{ color: "var(--accent)" }}>
                        FAQ
                    </div>
                    <h2
                        data-testid="faq-heading"
                        className="display-text font-black uppercase"
                        style={{ fontSize: "clamp(2rem, 6vw, 4.5rem)", lineHeight: 1, letterSpacing: "-0.03em" }}
                    >
                        Questions,<br />answered.
                    </h2>
                </FadeIn>
                <div className="max-w-[900px]">
                    {FAQS.map((f, i) => (
                        <FaqItem
                            key={f.q}
                            index={i}
                            question={f.q}
                            answer={f.a}
                            isOpen={open === i}
                            onToggle={() => setOpen(open === i ? -1 : i)}
                        />
                    ))}
                </div>
            </div>
        </SectionFrame>
    );
}

function FaqItem({ index, question, answer, isOpen, onToggle }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: index * 0.06, ease: EASE }}
            className="border-b"
            style={{ borderColor: "var(--line)" }}
        >
            <button
                type="button"
                data-testid={`faq-question-${index}`}
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-6 py-6 md:py-7 text-left group"
            >
                <span
                    className="font-medium text-lg md:text-2xl transition-colors"
                    style={{
                        color: isOpen ? "var(--accent)" : "var(--text)",
                        fontFamily: "'Glacial Indifference', sans-serif",
                    }}
                >
                    {question}
                </span>
                <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className="flex-none w-9 h-9 md:w-10 md:h-10 rounded-full border flex items-center justify-center transition-colors group-hover:bg-white/5"
                    style={{
                        borderColor: isOpen ? "var(--accent)" : "var(--line)",
                        color: isOpen ? "var(--accent)" : "var(--muted)",
                        boxShadow: isOpen ? "0 0 18px var(--accent-soft)" : "none",
                    }}
                >
                    <Plus size={18} />
                </motion.span>
            </button>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        key="answer"
                        data-testid={`faq-answer-${index}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        className="overflow-hidden"
                    >
                        <div className="pb-7 md:pb-8 pr-2 md:pr-16 space-y-4">
                            {answer.map((p) => (
                                <p key={p.slice(0, 48)} className="text-base md:text-lg" style={{ color: "var(--muted)", lineHeight: 1.6 }}>
                                    {p}
                                </p>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
